import { useState } from 'react';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { ModalShell } from '../../shared/ModalShell';
import { useCreateVendor } from '../../../hooks/useVendors';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import {
    MAX_VENDOR_ID_LENGTH,
    MAX_VENDOR_NAME_LENGTH,
    VENDOR_ID_PATTERN
} from '../../../services/vendors';
import { ModalField, MODAL_INPUT_CLASSES } from '../../shared/ModalField';

// The slug is the primary key and appears in dispatch logs, so it is typed
// once at creation and never editable afterwards.
function slugify(name: string): string {
    return (
        name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .slice(0, MAX_VENDOR_ID_LENGTH)
            // Trimmed after the slice too: cutting at the cap can land on a hyphen.
            .replace(/^-+|-+$/g, '')
    );
}

interface CreateVendorModalProps {
    onClose: () => void;
    onCreated: (vendorId: string) => void;
}

export function CreateVendorModal({ onClose, onCreated }: CreateVendorModalProps) {
    const { showToast } = useToast();
    const { mutateAsync: createVendor, isPending } = useCreateVendor();

    const [name, setName] = useState('');
    const [id, setId] = useState('');
    const [idEdited, setIdEdited] = useState(false);

    const effectiveId = idEdited ? id.trim() : slugify(name);
    const trimmedName = name.trim();
    const nameValid = trimmedName.length > 0 && trimmedName.length <= MAX_VENDOR_NAME_LENGTH;
    const idValid = VENDOR_ID_PATTERN.test(effectiveId);
    const canSubmit = nameValid && idValid && !isPending;

    const submit = async () => {
        try {
            const result = await createVendor({
                id: effectiveId,
                name: trimmedName,
                isActive: true
            });
            showToast(`Vendor ${result.vendor.name} created. Add an ordering method`, 'success');
            if (result.warning) showToast(result.warning.message, 'error');
            onCreated(result.vendor.id);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to create the vendor'), 'error');
        }
    };

    return (
        <ModalShell
            widthClassName="max-w-[480px]"
            panelClassName="rounded-xl max-h-[88vh] overflow-y-auto p-6"
            dismissible={!isPending}
            onDismiss={onClose}
            labelledBy="vendor-create-title"
        >
            <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <BuildingStorefrontIcon className="h-5 w-5 text-gray-600" />
                </span>
                <div>
                    <h2 id="vendor-create-title" className="text-lg font-bold text-gray-800">
                        New vendor
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Vendors cannot be deleted once products reference them.
                    </p>
                </div>
            </div>

            <div className="mt-5 flex flex-col gap-4">
                <ModalField htmlFor="vendor-new-name" label="Name" required>
                    <input
                        id="vendor-new-name"
                        type="text"
                        value={name}
                        autoFocus
                        maxLength={MAX_VENDOR_NAME_LENGTH}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Biolux Group"
                        className={MODAL_INPUT_CLASSES}
                    />
                </ModalField>

                <ModalField
                    htmlFor="vendor-new-id"
                    label="Slug"
                    required
                    hintTone={idValid || effectiveId.length === 0 ? 'muted' : 'error'}
                    hint={
                        effectiveId.length > 0 && !idValid
                            ? 'Lowercase letters, digits and single hyphens only.'
                            : 'Permanent, and what appears in dispatch logs and PO rows.'
                    }
                >
                    <input
                        id="vendor-new-id"
                        type="text"
                        value={effectiveId}
                        maxLength={MAX_VENDOR_ID_LENGTH}
                        onChange={(e) => {
                            setIdEdited(true);
                            setId(e.target.value);
                        }}
                        placeholder="biolux"
                        className={`${MODAL_INPUT_CLASSES} font-mono`}
                    />
                </ModalField>

                <p className="text-xs text-gray-400">
                    Ordering methods and their recipients are added once the vendor exists.
                </p>
            </div>

            <div className="flex justify-end items-center gap-3 mt-6">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={isPending}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={() => {
                        void submit();
                    }}
                    disabled={!canSubmit}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isPending ? 'Creating…' : 'Create vendor'}
                </button>
            </div>
        </ModalShell>
    );
}
